<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as NewAirRoadCalculateVolumeDialogFormInitialData,
  ValidData as NewAirRoadCalculateVolumeDialogFormValidData,
} from '@/components/dialogForms/NewAirRoadCalculateVolumeDialogForm.vue';

import { nextTick, reactive, ref, useTemplateRef, watch } from 'vue';
import _isEqual from 'lodash/isEqual';

import KBtn from '@/components/btns/KBtn.vue';
import NewAirRoadCalculateVolumeDialogForm from '@/components/dialogForms/NewAirRoadCalculateVolumeDialogForm.vue';
import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import KNumberField from '@/components/fields/KNumberField.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import NewAirRoadPopoverVMenu from '@/components/vMenus/NewAirRoadPopoverVMenu.vue';
import useForm from '@/composables/useForm';
import {
  nonZeroPositiveDecimalRequiredValidationRule,
  requiredValidationRule,
} from '@/helpers/validationRules';

type Size90x90x120Preset = 'size90x90x120Preset';
type Size40x40x30Preset = 'size40x40x30Preset';
type Size10x10x30Preset = 'size10x10x30Preset';
type SizeCustomPreset = 'sizeCustomPreset';

export interface InitialData {
  sizePreset?:
    | Size90x90x120Preset
    | Size40x40x30Preset
    | Size10x10x30Preset
    | SizeCustomPreset
    | null;
  quantity?: number;
  isSplit?: boolean;
  splitCount?: number;
}

export interface ValidData {
  volume: number;
  quantity: number;
  splitCount: number;
}

export interface Props {
  maxQuantity?: number;
  submitText?: string;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  maxQuantity: 0,
  submitText: 'Pack',
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const size90x90x120Preset: Size90x90x120Preset = 'size90x90x120Preset';
const size40x40x30Preset: Size40x40x30Preset = 'size40x40x30Preset';
const size10x10x30Preset: Size10x10x30Preset = 'size10x10x30Preset';
const sizeCustomPreset: SizeCustomPreset = 'sizeCustomPreset';

const formData = reactive<InitialData>({
  sizePreset: size40x40x30Preset,
  splitCount: 1,
});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  if (formData.quantity == null) return;
  if (formData.splitCount == null) return;
  const validData: ValidData = {
    volume: volume.value,
    quantity: formData.quantity,
    splitCount: formData.splitCount,
  };
  emit('submit', event, validData);
});

const isCalculateMenu = ref(false);

const volumeInitialData =
  reactive<NewAirRoadCalculateVolumeDialogFormInitialData>({});

const _isSizePreset = ref(false);
const _isVolume = ref(false);

const volume = ref(0);

function calculateVolume(): void {
  if (
    volumeInitialData.width == null ||
    volumeInitialData.height == null ||
    volumeInitialData.length == null
  ) {
    volume.value = 0;
  } else {
    const calculatedValue =
      (volumeInitialData.width *
        volumeInitialData.height *
        volumeInitialData.length) /
      1000000;
    volume.value = Math.ceil(calculatedValue * 1000) / 1000;
  }
}

watch(
  function () {
    return formData.sizePreset;
  },
  function (newValues, oldValues) {
    if (_isEqual(newValues, oldValues)) return;
    if (newValues === size90x90x120Preset) {
      Object.assign(volumeInitialData, { width: 90, height: 90, length: 120 });
      calculateVolume();
    } else if (newValues === size40x40x30Preset) {
      Object.assign(volumeInitialData, { width: 40, height: 40, length: 30 });
      calculateVolume();
    } else if (newValues === size10x10x30Preset) {
      Object.assign(volumeInitialData, { width: 10, height: 10, length: 30 });
      calculateVolume();
    } else if (newValues === sizeCustomPreset) {
      // open when `sizeCustomPreset` and when not all zeros after `handleVolumeInput`
      if (
        volumeInitialData.width !== 0 &&
        volumeInitialData.height !== 0 &&
        volumeInitialData.length !== 0
      ) {
        isCalculateMenu.value = true;
      }
    }
  },
  { immediate: true },
);

watch(
  function () {
    return formData.isSplit;
  },
  function (newValues, oldValues) {
    if (_isEqual(newValues, oldValues)) return;
    if (!formData.isSplit) formData.splitCount = 1;
  },
  { immediate: true },
);

function handleCalculateClick(): void {
  isCalculateMenu.value = !isCalculateMenu.value;
}

function handleCalculateVolumeSubmit(
  event: SubmitEventPromise | null | undefined,
  volumeFormData: NewAirRoadCalculateVolumeDialogFormValidData,
  isFromRadioPreset: boolean,
  isFromInputVolume: boolean,
): void {
  Object.assign(volumeInitialData, volumeFormData);
  if (!isFromInputVolume) calculateVolume();
  if (!isFromRadioPreset) formData.sizePreset = sizeCustomPreset;
  // close on Enter (manual submit)
  if (event) {
    // close right after `sizeCustomPreset`
    void nextTick(function () {
      isCalculateMenu.value = false;
    });
  }
}

function handleSizePresetInput(event: InputEvent): void {
  _isSizePreset.value = true;
}

function handleSizePresetBlur(event: FocusEvent): void {
  _isSizePreset.value = false;
}

function handleVolumeInput(event: InputEvent): void {
  _isVolume.value = true;
  volumeInitialData.width = 0;
  volumeInitialData.height = 0;
  volumeInitialData.length = 0;
  formData.sizePreset = sizeCustomPreset;
  // close right after `sizeCustomPreset`
  void nextTick(function () {
    isCalculateMenu.value = false;
  });
}

function handleVolumeBlur(event: FocusEvent): void {
  _isVolume.value = false;
}

const volumeTextFieldEl = useTemplateRef('volumeTextField');

function customValidationRule(value?: unknown): boolean | string {
  if (!formData.splitCount) return true;
  if (!formData.quantity) return true;
  if (formData.splitCount > formData.quantity) {
    return "There can't be more packages than products";
  } else {
    return true;
  }
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.newAirRoadPackageTableForm"
    data-testid="newAirRoadPackageTableForm"
    @submit.prevent="handleSubmit"
  >
    <div :class="$style.lineGroup">
      <div :class="$style.groupItem">
        <div>
          <v-radio-group
            v-model="formData.sizePreset"
            :rules="[requiredValidationRule]"
            :class="$style.radioGroupSmall"
            label="Size"
            hide-details
          >
            <KRadioField
              label="90 x 90 x 120"
              :value="size90x90x120Preset"
              size="xs"
              @input="handleSizePresetInput"
              @blur="handleSizePresetBlur"
            />
            <KRadioField
              label="40 x 40 x 30"
              :value="size40x40x30Preset"
              size="xs"
              @input="handleSizePresetInput"
              @blur="handleSizePresetBlur"
            />
            <KRadioField
              label="10 x 10 x 30"
              :value="size10x10x30Preset"
              size="xs"
              @input="handleSizePresetInput"
              @blur="handleSizePresetBlur"
            />
            <KRadioField
              label="Custom"
              :value="sizeCustomPreset"
              size="xs"
              @input="handleSizePresetInput"
              @blur="handleSizePresetBlur"
            />
          </v-radio-group>
        </div>
      </div>
      <div :class="$style.groupItem">
        <div>
          <KBtn
            priority="info"
            variant="text"
            prepend-icon="mdi-calculator"
            size="xs"
            block
            :active="isCalculateMenu"
            class="mb-2"
            @click="handleCalculateClick"
          >
            Calculate
          </KBtn>
        </div>
        <div>
          <KTextField
            ref="volumeTextField"
            v-model="volume"
            size="sm"
            label="Volume (m³)"
            :rules="[nonZeroPositiveDecimalRequiredValidationRule]"
            @input="handleVolumeInput"
            @blur="handleVolumeBlur"
          />
        </div>
      </div>
    </div>
    <div :class="$style.lineGroup">
      <div :class="$style.groupItem">
        <div>
          <KNumberField
            v-model="formData.quantity"
            clearable
            :hint="'of ' + props.maxQuantity + ' items'"
            label="Quantity"
            :max="props.maxQuantity"
            :min="formData.splitCount"
            persistent-hint
            :rules="[requiredValidationRule, customValidationRule]"
            size="sm"
          />
        </div>
      </div>
      <div :class="[$style.groupItem, $style.groupItemSplit]">
        <div>
          <KCheckboxField
            v-model="formData.isSplit"
            :class="$style.checkboxSmallField"
            hide-details
            label="Split into"
            size="xs"
          >
          </KCheckboxField>
        </div>
        <div>
          <KNumberField
            v-model="formData.splitCount"
            clearable
            :disabled="!formData.isSplit"
            label="Packages"
            :max="formData.quantity"
            :min="1"
            :rules="[requiredValidationRule, customValidationRule]"
            size="sm"
          />
        </div>
      </div>
    </div>
    <div :class="$style.footer">
      <KBtn
        type="submit"
        prepend-icon="mdi-archive-plus-outline"
        :class="$style.submit"
        size="sm"
        priority="info"
      >
        {{ props.submitText }}
      </KBtn>
    </div>
    <NewAirRoadPopoverVMenu
      v-model="isCalculateMenu"
      :close-on-content-click="false"
      location="end"
      no-click-animation
      persistent
      scroll-strategy="none"
      :target="volumeTextFieldEl"
    >
      <template #header>Dimensions (centimeters):</template>
      <NewAirRoadCalculateVolumeDialogForm
        :initial-data="volumeInitialData"
        @submit="
          (event, volumeFormData) =>
            handleCalculateVolumeSubmit(
              event,
              volumeFormData,
              _isSizePreset,
              _isVolume,
            )
        "
      />
    </NewAirRoadPopoverVMenu>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.newAirRoadPackageTableForm {
}
  .radioGroupSmall {
    :global {
      .v-input__control > .v-label {
        margin-left: 0;
        opacity: 1;
      }
      .v-input__control > .v-label + .v-selection-control-group {
        padding-left: 0;
      }
    }
  }
  .checkboxSmallField {
    margin-bottom: 8px;
  }
  .lineGroup {
    display: flex;
    padding: 8px 0 0 0;
    gap: 16px;
  }
    .groupItem {
      width: 50%;
    }
    .groupItemSplit {
      margin-top: -#{variables.$btn-xs-height + 8px}
    }
  .footer {
    text-align: right;
  }
</style>
